/**
 * Resolves the destination for "Participar en predicciones" CTA.
 *
 * Priority:
 *   1. Live match → Match Viewer (home `/` with `?open=predictions`).
 *   2. Upcoming match → Centro de Predicciones (`/quinielas`).
 *   3. Fallback → Selector de partidos (`/quinielas`).
 *
 * Never returns the bare home as a generic landing — when `/` is used it
 * is because there is an active match to view, and `open=predictions`
 * auto-opens the Prediction Hub on top of it.
 */
import { supabase } from "@/integrations/supabase/client";

export type ParticipateTarget =
  | { kind: "match_viewer"; to: "/"; search: { open: "predictions" }; reason: "live_match" }
  | { kind: "predictions_center"; to: "/quinielas"; reason: "upcoming_match" | "fallback" };

export async function resolveParticipateRoute(): Promise<ParticipateTarget> {
  try {
    const { data: live } = await supabase
      .from("bracket_matches")
      .select("id,status,kickoff_at")
      .eq("status", "live")
      .order("kickoff_at", { ascending: true })
      .limit(1);
    if (live && live.length > 0) {
      return { kind: "match_viewer", to: "/", search: { open: "predictions" }, reason: "live_match" };
    }
    const { data: upcoming } = await supabase
      .from("bracket_matches")
      .select("id")
      .eq("status", "scheduled")
      .gte("kickoff_at", new Date().toISOString())
      .order("kickoff_at", { ascending: true })
      .limit(1);
    if (upcoming && upcoming.length > 0) {
      return { kind: "predictions_center", to: "/quinielas", reason: "upcoming_match" };
    }
  } catch {
    // network/db hiccup → fall through to selector
  }
  return { kind: "predictions_center", to: "/quinielas", reason: "fallback" };
}